import { useEffect, useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { BillingBar } from "./BillingBar";
import { OptionGroup } from "./OptionGroup";
import { SizePicker } from "./SizePicker";
import { CostEstimate } from "./CostEstimate";
import { ViewControls } from "./ViewControls";
import { PresetManager } from "./PresetManager";
import { PanelResizeHandle } from "./PanelResizeHandle";
import type { Background, Count, Format, Quality } from "../types";

const QUALITY_ITEMS = [
  { value: "low" as const, label: "낮음", sub: "~10–20초" },
  { value: "medium" as const, label: "중간", sub: "~20–40초" },
  { value: "high" as const, label: "높음", sub: "~40–80초" },
];

const FORMAT_ITEMS = [
  { value: "png" as const, label: "PNG", sub: "투명 지원" },
  { value: "jpeg" as const, label: "JPEG", sub: "작은 용량" },
  { value: "webp" as const, label: "WebP", sub: "균형" },
];

const BACKGROUND_ITEMS = [
  { value: "auto" as const, label: "자동", sub: "내용에 맞춤" },
  { value: "opaque" as const, label: "불투명", sub: "일반 배경" },
  { value: "transparent" as const, label: "투명", sub: "PNG/WebP" },
];

const MOD_ITEMS = [
  { value: "auto" as const, label: "표준", sub: "보수적" },
  {
    value: "low" as const,
    label: "완화",
    sub: "기본값 · 경계선 허용",
    color: "var(--amber)",
  },
];

const COUNT_ITEMS: { value: string; label: string; sub?: string }[] = [
  { value: "1", label: "1", sub: "기본" },
  { value: "2", label: "2", sub: "×2 비용" },
  { value: "4", label: "4", sub: "×4 비용" },
];

function ModerationHelp() {
  return (
    <span
      className="option-help-icon"
      title="완화(기본값)는 경계선 프롬프트(수영복·강한 액션 등)를 더 많이 허용합니다. 표준은 OpenAI 기본 필터로 조금 더 보수적이며, 유해 콘텐츠(미성년자·포르노·극단적 폭력 등) 하드 차단은 두 모드 모두 동일합니다."
      aria-label="모더레이션 안내"
    >
      ?
    </span>
  );
}

export function RightPanel() {
  const open = useAppStore((s) => s.rightPanelOpen);
  const toggle = useAppStore((s) => s.toggleRightPanel);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 800px)").matches : false,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 800px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const drawerOpen = isMobile ? open : true;

  const quality = useAppStore((s) => s.quality);
  const setQuality = useAppStore((s) => s.setQuality);
  const format = useAppStore((s) => s.format);
  const setFormat = useAppStore((s) => s.setFormat);
  const moderation = useAppStore((s) => s.moderation);
  const setModeration = useAppStore((s) => s.setModeration);
  const count = useAppStore((s) => s.count);
  const setCount = useAppStore((s) => s.setCount);
  const maxAttempts = useAppStore((s) => s.maxAttempts);
  const setMaxAttempts = useAppStore((s) => s.setMaxAttempts);
  const background = useAppStore((s) => s.background);
  const setBackground = useAppStore((s) => s.setBackground);
  const compression = useAppStore((s) => s.compression);
  const setCompression = useAppStore((s) => s.setCompression);

  return (
    <>
      {isMobile && open ? (
        <div
          className="right-panel-backdrop"
          role="button"
          aria-label="설정 닫기"
          onClick={toggle}
        />
      ) : null}
      <aside
        className={`right-panel${open ? "" : " collapsed"}${isMobile && drawerOpen ? " drawer-open" : ""}`}
        aria-label="세부 설정"
      >
        <button
          type="button"
          className="right-panel-toggle"
          aria-expanded={open}
          aria-controls="right-panel-body"
          onClick={toggle}
          title={open ? "설정 숨기기" : "설정 보기"}
        >
          {isMobile ? (
            <span>{open ? "닫기" : "설정"}</span>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points={open ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
            </svg>
          )}
        </button>
        {!isMobile && open ? <PanelResizeHandle target="right-panel" /> : null}
        <div
          id="right-panel-body"
          className="right-panel-body"
          hidden={!open}
        >
          <ViewControls />
          <PresetManager />
          <BillingBar />
          <OptionGroup<Quality>
            title="품질"
            hint="요청값입니다. 실제 화질은 서버가 정하며 대개 low~medium으로 적용됩니다."
            items={QUALITY_ITEMS}
            value={quality}
            onChange={setQuality}
          />
          <SizePicker />
          <OptionGroup<Format>
            title="포맷"
            items={FORMAT_ITEMS}
            value={format}
            onChange={setFormat}
          />
          <OptionGroup<Background>
            title="배경"
            hint="투명 배경은 현재 경로에서 적용되지 않고 불투명으로 처리됩니다."
            items={BACKGROUND_ITEMS}
            value={background}
            onChange={setBackground}
          />
          {format !== "png" ? (
            <div className="option-group">
              <div className="section-title">압축 품질</div>
              <label className="compression-control">
                <input
                  type="range"
                  aria-label="압축 품질"
                  min={0}
                  max={100}
                  value={compression}
                  onInput={(event) => setCompression(Number(event.currentTarget.value))}
                  onChange={(event) => setCompression(Number(event.target.value))}
                />
                <output>{compression}</output>
              </label>
              <div className="option-sub">높을수록 선명하고 파일이 커집니다.</div>
            </div>
          ) : null}
          <div className="option-group">
            <div className="section-title option-title-with-help">
              모더레이션
              <ModerationHelp />
            </div>
            <div className="option-row">
              {MOD_ITEMS.map((it) => (
                <button
                  key={it.value}
                  className={`option-btn${it.value === moderation ? " active" : ""}`}
                  style={it.color ? { color: it.color } : undefined}
                  onClick={() => setModeration(it.value)}
                  type="button"
                >
                  {it.label}
                  {it.sub ? (
                    <>
                      <br />
                      <span className="option-sub">{it.sub}</span>
                    </>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
          <OptionGroup<string>
            title="개수"
            items={COUNT_ITEMS}
            value={String(count)}
            onChange={(v) => setCount(Number(v) as Count)}
          />
          <div className="option-group">
            <div className="section-title option-title-with-help">
              최대 재시도 횟수
              <span
                className="option-help-icon"
                title="생성 실패 시 최대 몇 번까지 반복 시도할지 설정합니다. 총 시도 횟수(첫 시도 포함)이며 1~10 사이로 설정할 수 있습니다. 모든 시도가 실패하면 로그에 기록되고 재시도 버튼으로 다시 돌릴 수 있습니다."
                aria-label="재시도 안내"
              >
                ?
              </span>
            </div>
            <div className="option-row" style={{ alignItems: "center", gap: 8 }}>
              <input
                type="number"
                min={1}
                max={10}
                step={1}
                value={maxAttempts}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) setMaxAttempts(n);
                }}
                style={{
                  width: 72,
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                  background: "var(--surface)",
                  color: "inherit",
                  fontSize: 14,
                }}
                aria-label="최대 재시도 횟수"
              />
              <span className="option-sub">회 (총 시도)</span>
            </div>
          </div>
          <CostEstimate />
        </div>
      </aside>
    </>
  );
}
